import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Input, notification, Skeleton, Tag, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import { getMenus, getMenuStores, markMenuOutOfStock, restockMenu } from '../../services/outofstockservice';
import { MenuItem, StoreStatus } from '../../types/outofstocktypes';
import StoreSelectorDrawer from './storeselectordrawer';
import { useGlobalLoading } from '../common/globalloadingcontext';
import FormButton from '../common/formbutton';

const { Search } = Input;

interface MenusTabProps {
    userGuid: string;
    activeTab: string;
}

const MenusTab: React.FC<MenusTabProps> = ({ userGuid, activeTab }) => {
    const { setGlobalLoading } = useGlobalLoading();
    const [menuData, setMenuData] = useState<MenuItem[]>([]);
    const [menuTotalCount, setMenuTotalCount] = useState(0);
    const [menuLoading, setMenuLoading] = useState(false);
    const [menuSearch, setMenuSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Drawer state
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
    const [storesData, setStoresData] = useState<StoreStatus[]>([]);
    const [currentAction, setCurrentAction] = useState<'out' | 'restock' | null>(null);

    const fetchMenus = async (pageNumber = 1, size = pageSize, search = menuSearch) => {
        setMenuLoading(true);
        try {
            const res = await getMenus(pageNumber, size, search);
            setMenuData(res.data.items);
            setMenuTotalCount(res.data.totalCount);
            setCurrentPage(pageNumber);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load menus.' });
        } finally {
            setMenuLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setMenuSearch(value);
            fetchMenus(1, pageSize, value);
        }, 400),
        [pageSize]
    );

    useEffect(() => {
        if (activeTab === 'menus') {
            fetchMenus(currentPage, pageSize, menuSearch);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchMenus(currentPage, pageSize, menuSearch);
    }, [pageSize]);

    const handleManageStores = async (menu: MenuItem, action: 'out' | 'restock') => {
        setDrawerLoading(true);
        setDrawerVisible(true);
        setSelectedMenu(menu);
        setCurrentAction(action);

        try {
            // Fetch stores filtered by action
            const res = await getMenuStores(
                menu.menuGuid,
                undefined,
                action === 'out' ? 'InStock' : 'OutOfStock'
            );
            setStoresData(res.data);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load stores.' });
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleConfirmStores = async (selectedStoreGuids: string[], action: 'out' | 'restock') => {
        if (!selectedMenu) return;

        setGlobalLoading(true);
        try {
            const request = {
                menuGuid: selectedMenu.menuGuid,
                storeGuids: selectedStoreGuids,
                userGuid: userGuid,
                reason: action === 'out' ? 'Marked out of stock via UI' : 'Restocked via UI',
            };

            if (action === 'out') {
                await markMenuOutOfStock(request);
            } else {
                await restockMenu(request);
            }

            await fetchMenus(currentPage, pageSize, menuSearch);

            notification.success({
                message: 'Success',
                description: `${selectedMenu.name} updated for ${selectedStoreGuids.length} stores (action: ${action}).`,
            });
        } catch (err) {
            notification.error({
                message: 'Error',
                description: `Failed to update ${selectedMenu.name}.`,
            });
        } finally {
            setGlobalLoading(false);
            setDrawerVisible(false);
            setSelectedMenu(null);
        }
    };

    const menuColumns: ColumnsType<MenuItem> = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Stores',
            key: 'stores',
            render: (_: unknown, record: MenuItem) => (
                <Space style={{ display: 'flex', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 120 }}>Total Stores: {record.totalStores}</div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <Tag color="green">Available in {record.inStockCount} stores</Tag>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <Tag color="red">Out of stock in {record.outOfStockCount} stores</Tag>
                    </div>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col>
                    <Search
                        placeholder="Search menus"
                        onChange={e => debouncedSearch(e.target.value)}
                        style={{ width: 350 }}
                        allowClear
                    />
                </Col>
            </Row>

            {menuLoading && menuData.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<MenuItem>
                    dataSource={menuData}
                    columns={menuColumns}
                    loading={menuLoading && menuData.length > 0}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: menuTotalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page, size) => {
                            setPageSize(size);
                            fetchMenus(page, size, menuSearch);
                        },
                    }}
                    rowKey="menuGuid"
                />
            )}
            {selectedMenu && currentAction && (
                <StoreSelectorDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onOk={handleConfirmStores}
                    itemName={selectedMenu.name}
                    stores={storesData}
                    loading={drawerLoading}
                    action={currentAction}
                />
            )}

        </>
    );
};

export default MenusTab;